import {
  Button,
  Divider,
  IconButton,
  LinearProgress,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useState } from "react";
import { Address, ClientRecord } from "../repositories/types";
import { LookupAddress } from "../api/LocationAPI";

interface ClientAddressPopupProps {
  open: boolean;
  handleClose: (address: Address | undefined) => void;
  client: ClientRecord;
}

const useStyles = makeStyles((theme) => ({
  popoverRoot: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  gridRoot: {
    padding: "1em",
  },
}));

const ClientAddressPopup: React.FC<ClientAddressPopupProps> = (props: ClientAddressPopupProps) => {
  const classes = useStyles();
  const [address, setAddress] = useState<Address>();
  const [lookupAddress, setLookupAddress] = useState("");
  const [lookupPostcode, setLookupPostcode] = useState("");
  const [isSearching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const { open, handleClose, client } = props;

  useEffect(() => {
    setAddress({
      address: client.address,
      addressLine2: client.addressLine2,
      town: client.town,
      county: client.county,
      postCode: client.postCode,
    });
  }, [client]);

  const search = () => {
    setSearching(true);
    LookupAddress(lookupPostcode, lookupAddress).then((res: Address | null) => {
      setSearching(false);
      if (res !== null) {
        setAddress(res);
      } else {
        setNotFound(true);
      }
    });
  };

  return (
    <Popover
      open={open}
      anchorReference={"none"}
      onClose={() => handleClose(undefined)}
      classes={{
        root: classes.popoverRoot,
      }}
    >
      <Grid container direction="column" spacing={1} className={classes.gridRoot}>
        <Grid>
          <Typography variant="h5">Address Details</Typography>
        </Grid>
        <Grid container direction="column">
          <Grid>
            <Typography variant="subtitle1">Lookup Address</Typography>
          </Grid>
          <Grid container>
            <Grid size={{ xs: 8 }}>
              <TextField
                autoComplete="off"
                label="First line of Address"
                value={lookupAddress}
                onChange={(e) => { setNotFound(false); setLookupAddress(e.target.value); }}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField
                label="Post Code"
                autoComplete="off"
                value={lookupPostcode}
                onChange={(e) => { setNotFound(false); setLookupPostcode(e.target.value); }}
              />
            </Grid>
            <Grid size={{ xs: 1 }}>
              <IconButton onClick={search} size="large" aria-label="search address">
                <SearchIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Grid>{isSearching && <LinearProgress />}</Grid>
        </Grid>
        <Grid>
          <Divider />
        </Grid>
        <Grid container direction="column">
          {notFound ? (
            <Typography variant="subtitle1" style={{ color: "red" }}>
              Address not found please enter manually
            </Typography>
          ) : (
            <Typography variant="subtitle1">or enter manually</Typography>
          )}
          <TextField
            label="Address"
            value={address?.address ?? ""}
            onChange={(e) => setAddress({ ...address, address: e.target.value })}
          />
          <TextField
            label="Address Line 2"
            value={address?.addressLine2 ?? ""}
            onChange={(e) => setAddress({ ...address, addressLine2: e.target.value })}
          />
          <TextField
            label="Town"
            value={address?.town ?? ""}
            onChange={(e) => setAddress({ ...address, town: e.target.value })}
          />
          <TextField
            label="County"
            value={address?.county ?? ""}
            onChange={(e) => setAddress({ ...address, county: e.target.value })}
          />
          <TextField
            label="Post Code"
            value={address?.postCode ?? ""}
            onChange={(e) => setAddress({ ...address, postCode: e.target.value })}
          />
        </Grid>
        <Grid container justifyContent="flex-end">
          <Button color="primary" onClick={() => handleClose(undefined)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleClose(address)}>
            Save
          </Button>
        </Grid>
      </Grid>
    </Popover>
  );
};

export default ClientAddressPopup;
